import React, { useState, useCallback } from 'react';
import { useConfirm } from '../contexts/ConfirmContext';

interface TerritoryFixerPanelProps {
  apiBaseUrl: string;
  adminKey: string;
}

interface FixResult {
  status: string;
  message: string;
  island?: {
    id: string;
    name: string;
    ownerId: string | null;
    ownerType: string | null;
    ownerName: string | null;
  };
}

export const TerritoryFixerPanel: React.FC<TerritoryFixerPanelProps> = ({ apiBaseUrl, adminKey }) => {
  const [islandId, setIslandId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [result, setResult] = useState<FixResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { confirm } = useConfirm();

  const fixRegimeTerritory = useCallback(async (force: boolean = false): Promise<void> => {
    if (!islandId.trim()) {
      setError('Island ID is required');
      return;
    }

    const confirmed = await confirm({
      title: 'Convert to Regime Territory',
      message: force 
        ? `Convert island "${islandId}" to Regime territory? This will OVERWRITE existing ownership!`
        : `Convert island "${islandId}" to Regime territory? This will set the island as Regime-owned with a procedurally generated name.`,
      tone: force ? 'danger' : 'default',
    });

    if (!confirmed) return;

    try {
      setIsLoading(true);
      setError(null);
      setResult(null);

      const response = await fetch(`${apiBaseUrl}/api/admin/prod/territory/set-owner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey,
        },
        body: JSON.stringify({
          islandId: islandId.trim(),
          mode: 'regime',
          force,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409 && !force) {
          const retry = await confirm({
            title: 'Island Already Claimed',
            message: 'Do you want to FORCE overwrite the existing ownership?',
            tone: 'danger',
          });
          if (retry) {
            await fixRegimeTerritory(true);
          }
          return;
        }
        throw new Error(data.error || 'Failed to fix territory');
      }

      setResult(data);
      setIslandId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [islandId, apiBaseUrl, adminKey, confirm]);

  const clearTerritory = useCallback(async (): Promise<void> => {
    if (!islandId.trim()) {
      setError('Island ID is required');
      return;
    }

    const confirmed = await confirm({
      title: 'Clear Territory Ownership',
      message: `Clear ownership for island "${islandId}"? This will remove all ownership data from the island.`,
      tone: 'danger',
    });

    if (!confirmed) return;

    try {
      setIsLoading(true);
      setError(null);
      setResult(null);

      const response = await fetch(`${apiBaseUrl}/api/admin/prod/territory/set-owner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey,
        },
        body: JSON.stringify({
          islandId: islandId.trim(),
          mode: 'clear',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to clear territory');
      }

      setResult(data);
      setIslandId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [islandId, apiBaseUrl, adminKey, confirm]);

  const fixArcadiaIslands = useCallback(async (): Promise<void> => {
    const confirmed = await confirm({
      title: 'Fix All Arcadia Islands',
      message: 'This will set ALL islands in the Arcadia region to Regime ownership with visible names. This operation cannot be undone. Continue?',
      tone: 'danger',
    });

    if (!confirmed) return;

    try {
      setIsLoading(true);
      setError(null);
      setResult(null);

      const response = await fetch(`${apiBaseUrl}/api/admin/prod/territory/fix-arcadia`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fix Arcadia islands');
      }

      setResult({
        status: data.status,
        message: `${data.message} (${data.fixed} fixed, ${data.skipped} skipped)`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, adminKey, confirm]);

  return (
    <div
      style={{
        backgroundColor: '#1a1a2e',
        border: '1px solid #4a5568',
        borderRadius: 0,
        padding: 16,
        marginBottom: 16,
        fontFamily: 'monospace',
      }}
    >
      <h3
        style={{
          color: '#f59e0b',
          fontSize: 14,
          margin: '0 0 16px 0',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 1,
        }}
      >
        [TERRITORY] REGIME FIXER
      </h3>

      <div style={{ marginBottom: 16 }}>
        <label
          style={{
            display: 'block',
            color: '#a0aec0',
            fontSize: 11,
            marginBottom: 6,
            textTransform: 'uppercase',
          }}
        >
          Island ID
        </label>
        <input
          type="text"
          value={islandId}
          onChange={(e) => setIslandId(e.target.value)}
          placeholder="island_3_3_4a08_3933"
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '8px 12px',
            backgroundColor: '#0b1220',
            border: '1px solid #4a5568',
            color: '#e2e8f0',
            fontSize: 12,
            fontFamily: 'monospace',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ color: '#718096', fontSize: 10, marginTop: 4 }}>
          Tip: Hover over an island in-game to see its ID in the tooltip
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => fixRegimeTerritory(false)}
          disabled={isLoading || !islandId.trim()}
          style={{
            flex: 1,
            padding: '10px 16px',
            backgroundColor: isLoading || !islandId.trim() ? '#2d3748' : '#f59e0b',
            color: isLoading || !islandId.trim() ? '#718096' : '#0d1117',
            border: 'none',
            fontSize: 11,
            fontWeight: 600,
            cursor: isLoading || !islandId.trim() ? 'not-allowed' : 'pointer',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {isLoading ? 'Processing...' : 'Convert to Regime'}
        </button>
        <button
          onClick={clearTerritory}
          disabled={isLoading || !islandId.trim()}
          style={{
            flex: 1,
            padding: '10px 16px',
            backgroundColor: isLoading || !islandId.trim() ? '#2d3748' : '#e53e3e',
            color: isLoading || !islandId.trim() ? '#718096' : '#ffffff',
            border: 'none',
            fontSize: 11,
            fontWeight: 600,
            cursor: isLoading || !islandId.trim() ? 'not-allowed' : 'pointer',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          Clear Ownership
        </button>
      </div>

      <div style={{ marginBottom: 16, paddingTop: 16, borderTop: '1px solid #4a5568' }}>
        <button
          onClick={fixArcadiaIslands}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '12px 16px',
            backgroundColor: isLoading ? '#2d3748' : '#8b5cf6',
            color: isLoading ? '#718096' : '#ffffff',
            border: 'none',
            fontSize: 12,
            fontWeight: 600,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {isLoading ? 'Processing...' : '🌊 Fix All Arcadia Islands (Bulk)'}
        </button>
        <div style={{ color: '#a0aec0', fontSize: 10, marginTop: 8 }}>
          Sets all Arcadia region islands to Regime ownership with visible names
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            backgroundColor: '#2d1b1b',
            border: '1px solid #e53e3e',
            borderRadius: 4,
            marginBottom: 16,
          }}
        >
          <div style={{ color: '#fc8181', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
            ERROR
          </div>
          <div style={{ color: '#feb2b2', fontSize: 11 }}>{error}</div>
        </div>
      )}

      {result && (
        <div
          style={{
            padding: 12,
            backgroundColor: '#1c2d1c',
            border: '1px solid #48bb78',
            borderRadius: 4,
          }}
        >
          <div style={{ color: '#68d391', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>
            SUCCESS
          </div>
          <div style={{ color: '#c6f6d5', fontSize: 11, marginBottom: 4 }}>{result.message}</div>
          {result.island && (
            <div
              style={{
                marginTop: 8,
                padding: 8,
                backgroundColor: '#0b1220',
                borderRadius: 2,
                fontSize: 10,
              }}
            >
              <div style={{ color: '#a0aec0', marginBottom: 4 }}>
                <strong>Island:</strong> {result.island.name}
              </div>
              <div style={{ color: '#a0aec0', marginBottom: 4 }}>
                <strong>Owner Type:</strong>{' '}
                <span
                  style={{
                    color: result.island.ownerType === 'faction_regime' ? '#f59e0b' : '#68d391',
                  }}
                >
                  {result.island.ownerType || 'none'}
                </span>
              </div>
              <div style={{ color: '#a0aec0' }}>
                <strong>Owner Name:</strong> {result.island.ownerName || 'none'}
              </div>
            </div>
          )}
        </div>
      )}

      <div
        style={{
          marginTop: 16,
          padding: 12,
          backgroundColor: '#0b1220',
          border: '1px solid #4a5568',
          borderRadius: 4,
        }}
      >
        <div style={{ color: '#fbbf24', fontSize: 11, fontWeight: 600, marginBottom: 8 }}>
          ℹ️ USAGE
        </div>
        <ul style={{ margin: 0, padding: '0 0 0 16px', color: '#a0aec0', fontSize: 10 }}>
          <li style={{ marginBottom: 4 }}>
            <strong>Convert to Regime:</strong> Sets island as regime-owned with procedurally
            generated name
          </li>
          <li style={{ marginBottom: 4 }}>
            <strong>Clear Ownership:</strong> Removes all ownership data (makes island unclaimed)
          </li>
          <li>
            <strong>Force Mode:</strong> If island is already claimed, you'll be prompted to force
            overwrite
          </li>
        </ul>
      </div>
    </div>
  );
};
